import React, { useEffect, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import Paginator from '../../components/ui/Paginator';
import { errorMessage } from '../../utils/access';
import { cardBrandIcon, formatDay, formatMoney, formatTotals, MonthlyTotal } from './featureUi';

const PAGE_SIZE = 20;

type Status = 'PRUEBA' | 'ACTIVA' | 'MOROSA' | 'SUSPENDIDA';

export const SUBSCRIPTION_STATUS: Record<Status, { label: string; css: string; icon: string }> = {
    PRUEBA: { label: 'En prueba', css: 'bg-info-subtle text-info-emphasis', icon: 'bi-gift' },
    ACTIVA: { label: 'Al día', css: 'bg-success-subtle text-success-emphasis', icon: 'bi-check-circle' },
    MOROSA: { label: 'Cobro pendiente', css: 'bg-warning-subtle text-warning-emphasis', icon: 'bi-exclamation-triangle' },
    SUSPENDIDA: { label: 'Suspendida', css: 'bg-danger-subtle text-danger-emphasis', icon: 'bi-slash-circle' },
};

export interface SubscriptionRow {
    merchant: { id: number; name: string; documentNumber: string | null; status: string };
    bank: { id: number; name: string | null } | null;
    status: Status;
    dueOn: string | null;
    trialEndsOn: string | null;
    nextChargeOn: string | null;
    amount: number;
    currency: string | null;
    users: { contracted: number; active: number; billed: number };
    card: { last4: string; brand: string | null; expiration: string | null } | null;
    failedAttempts: number;
    nextRetryAt: string | null;
    lastError: string | null;
    suspendedAt: string | null;
    lastPayment: { at: string; amount: number | null } | null;
    createdAt: string | null;
}

interface Summary {
    byStatus: Record<Status, number>;
    monthlyRecurring: MonthlyTotal[];
    trialPotential: MonthlyTotal[];
    overdue: MonthlyTotal[];
    noCard: number;
    dueIn7Days: number;
}

interface ReportResponse { subscriptions: SubscriptionRow[]; total: number; summary: Summary; }

interface Charge {
    at: string; periodFrom: string; periodTo: string; attempt: number; origin: string; amount: number; currency: string;
    users: number; status: string; message: string | null; cardLast4: string | null; authCode: string | null;
}

const CHARGE_STATUS: Record<string, string> = {
    APROBADO: 'text-success', RECHAZADO: 'text-danger', ERROR: 'text-warning-emphasis', PENDIENTE: 'text-muted', SIN_CARGO: 'text-muted',
};

const daysUntil = (day: string | null) => {
    if (!day) return null;
    const [y, m, d] = day.split('-').map(Number);
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((new Date(y, m - 1, d).getTime() - start.getTime()) / 86_400_000);
};

const dueLabel = (row: SubscriptionRow) => {
    const days = daysUntil(row.dueOn);
    if (days === null) return '';
    if (row.status === 'SUSPENDIDA') return 'Sin pagar';
    if (days < 0) return `Vencido hace ${-days} ${-days === 1 ? 'día' : 'días'}`;
    if (days === 0) return 'Hoy';
    return `En ${days} ${days === 1 ? 'día' : 'días'}`;
};

const csvCell = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Reportes > Suscripciones (solo instancia Swiftmart / Swiftpay): comercios con suscripción del registro autogestionado.
 * Los demás bancos usan Funcionalidades por comercio. Estado, cuándo vence (fin de la prueba o próximo cobro), monto mensual, usuarios, tarjeta y último
 * pago; resumen de ingresos y alertas, filtros, historial de cobros por comercio y exportación a CSV.
 */
export const SubscriptionsReportPage = () => {
    const [filters, setFilters] = useState({ search: '', status: '', expiringDays: '', noCard: false });
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<ReportResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState<number | null>(null);
    const [charges, setCharges] = useState<Record<number, Charge[] | 'loading' | 'error'>>({});
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => (prev.search === search.trim() ? prev : { ...prev, search: search.trim() }));
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    const params = (pageIndex: number, pageSize: number) => ({
        pageIndex, pageSize,
        search: filters.search || undefined, status: filters.status || undefined,
        expiringDays: filters.expiringDays || undefined, noCard: filters.noCard ? '1' : undefined,
    });

    useEffect(() => {
        setLoading(true);
        setError('');
        topApi.get<ReportResponse>('/merchant-subscription/report', { params: params(page - 1, PAGE_SIZE) })
            .then(resp => setData(resp.data))
            .catch(e => { setError(errorMessage(e)); setData(null); })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page]);

    const setFilter = (patch: Partial<typeof filters>) => { setFilters(prev => ({ ...prev, ...patch })); setPage(1); };

    const toggle = (merchantId: number) => {
        setExpanded(prev => (prev === merchantId ? null : merchantId));
        if (charges[merchantId] && charges[merchantId] !== 'error') return;
        setCharges(prev => ({ ...prev, [merchantId]: 'loading' }));
        topApi.get<{ charges: Charge[] }>(`/merchant-subscription/report/${merchantId}/charges`)
            .then(resp => setCharges(prev => ({ ...prev, [merchantId]: resp.data.charges ?? [] })))
            .catch(() => setCharges(prev => ({ ...prev, [merchantId]: 'error' })));
    };

    // Todo el filtro (no solo la página) a CSV
    const exportCsv = async () => {
        setExporting(true);
        try {
            const rows: SubscriptionRow[] = [];
            for (let pageIndex = 0; ; pageIndex++) {
                const resp = await topApi.get<ReportResponse>('/merchant-subscription/report', { params: params(pageIndex, 100) });
                rows.push(...resp.data.subscriptions);
                if (rows.length >= resp.data.total || resp.data.subscriptions.length === 0) break;
            }
            const header = ['Comercio', 'Identificación', 'Estado', 'Vence', 'Monto mensual', 'Moneda',
                'Usuarios contratados', 'Usuarios activos', 'Tarjeta', 'Intentos fallidos', 'Último error', 'Último pago', 'Registro'];
            const lines = rows.map(r => [
                r.merchant.name, r.merchant.documentNumber, SUBSCRIPTION_STATUS[r.status]?.label ?? r.status,
                r.dueOn, r.amount.toFixed(2), r.currency, r.users.contracted, r.users.active, r.card ? `**** ${r.card.last4}` : 'Sin tarjeta',
                r.failedAttempts, r.lastError, r.lastPayment ? r.lastPayment.at.slice(0, 10) : '', r.createdAt ? r.createdAt.slice(0, 10) : '',
            ].map(csvCell).join(','));
            const blob = new Blob(['﻿' + [header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `suscripciones-${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setExporting(false);
        }
    };

    const summary = data?.summary;
    const totalPage = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));
    const columns = 7;

    const tile = (label: string, value: React.ReactNode, hint?: React.ReactNode, onClick?: () => void, active?: boolean) => (
        <div className="col-sm-6 col-xl-3">
            <div className={`card border-0 shadow-sm h-100 ${active ? 'border border-primary' : ''}`}
                role={onClick ? 'button' : undefined} onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
                <div className="card-body">
                    <div className="text-muted small">{label}</div>
                    <div className="fs-4 fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
                    {hint && <div className="small text-muted">{hint}</div>}
                </div>
            </div>
        </div>
    );

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-4">
                <div>
                    <h3 className="mb-1">Suscripciones</h3>
                    <p className="text-muted mb-0">
                        Comercios registrados en Swiftmart con suscripción: estado, cuándo vence y cuánto pagan al mes.
                    </p>
                </div>
                <button type="button" className="btn btn-outline-secondary" disabled={exporting || !data?.total} onClick={exportCsv}>
                    {exporting ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-download me-1" />}Exportar CSV
                </button>
            </div>

            {/* RESUMEN (todos los comercios del filtro) */}
            {summary && (
                <div className="row g-3 mb-3">
                    {tile('Ingreso mensual recurrente',
                        summary.monthlyRecurring.length ? formatTotals(summary.monthlyRecurring) : '-',
                        `${summary.byStatus.ACTIVA + summary.byStatus.MOROSA} suscripciones activas`)}
                    {tile('En periodo de prueba', summary.byStatus.PRUEBA,
                        summary.trialPotential.length ? `${formatTotals(summary.trialPotential)} al mes al terminar` : undefined,
                        () => setFilter({ status: filters.status === 'PRUEBA' ? '' : 'PRUEBA' }), filters.status === 'PRUEBA')}
                    {tile('Pendiente de cobro',
                        summary.overdue.length ? formatTotals(summary.overdue) : '-',
                        `${summary.byStatus.MOROSA} con cobro pendiente · ${summary.byStatus.SUSPENDIDA} suspendidas`,
                        () => setFilter({ status: filters.status === 'MOROSA' ? '' : 'MOROSA' }), filters.status === 'MOROSA')}
                    {tile('Requieren atención', summary.noCard + summary.dueIn7Days,
                        <>{summary.noCard} sin tarjeta · {summary.dueIn7Days} vencen en 7 días</>,
                        () => setFilter({ noCard: !filters.noCard }), filters.noCard)}
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input className="form-control" type="text" placeholder="Buscar por nombre o identificación..." aria-label="Buscar comercio"
                                    value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select" aria-label="Estado" value={filters.status} onChange={e => setFilter({ status: e.target.value })}>
                                <option value="">Todos los estados</option>
                                {(Object.keys(SUBSCRIPTION_STATUS) as Status[]).map(s => <option key={s} value={s}>{SUBSCRIPTION_STATUS[s].label}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select" aria-label="Vencimiento" value={filters.expiringDays} onChange={e => setFilter({ expiringDays: e.target.value })}>
                                <option value="">Cualquier vencimiento</option>
                                <option value="0">Vencen hoy</option>
                                <option value="7">Próximos 7 días</option>
                                <option value="15">Próximos 15 días</option>
                                <option value="30">Próximos 30 días</option>
                            </select>
                        </div>
                        <div className="col-md-3 d-flex align-items-center">
                            <div className="form-check form-switch mb-0">
                                <input id="report-no-card" className="form-check-input" type="checkbox" role="switch" checked={filters.noCard}
                                    onChange={e => setFilter({ noCard: e.target.checked })} />
                                <label className="form-check-label" htmlFor="report-no-card">Solo sin tarjeta</label>
                            </div>
                        </div>
                    </div>

                    {error && <div className="alert alert-danger small">{error}</div>}

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Comercio</th>
                                    <th>Estado</th>
                                    <th>Vence</th>
                                    <th className="text-end">Monto mensual</th>
                                    <th className="text-center">Usuarios</th>
                                    <th>Tarjeta</th>
                                    <th>Último pago</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {loading ? (
                                    <tr><td colSpan={columns} className="text-center py-5"><span className="spinner-border text-primary" /></td></tr>
                                ) : !data?.subscriptions.length ? (
                                    <tr><td colSpan={columns} className="text-center text-muted py-5">No hay comercios con suscripción para este filtro.</td></tr>
                                ) : data.subscriptions.map(row => {
                                    const status = SUBSCRIPTION_STATUS[row.status] ?? SUBSCRIPTION_STATUS.ACTIVA;
                                    const days = daysUntil(row.dueOn);
                                    const open = expanded === row.merchant.id;
                                    const history = charges[row.merchant.id];
                                    return (
                                        <React.Fragment key={row.merchant.id}>
                                            <tr style={{ cursor: 'pointer' }} onClick={() => toggle(row.merchant.id)} aria-expanded={open}>
                                                <td>
                                                    <div className="fw-medium">
                                                        <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'} small text-muted me-1`} />{row.merchant.name}
                                                    </div>
                                                    <div className="small text-muted">{row.merchant.documentNumber}</div>
                                                </td>
                                                <td>
                                                    <span className={`badge rounded-pill ${status.css}`}><i className={`bi ${status.icon} me-1`} />{status.label}</span>
                                                    {row.failedAttempts > 0 && row.status !== 'SUSPENDIDA' && (
                                                        <div className="small text-muted mt-1">{row.failedAttempts} {row.failedAttempts === 1 ? 'intento fallido' : 'intentos fallidos'}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div>{formatDay(row.dueOn)}</div>
                                                    <div className={`small ${row.status === 'SUSPENDIDA' || (days !== null && days < 0) ? 'text-danger' : days !== null && days <= 7 ? 'text-warning-emphasis' : 'text-muted'}`}>
                                                        {row.status === 'PRUEBA' ? 'Fin de la prueba · ' : ''}{dueLabel(row)}
                                                    </div>
                                                </td>
                                                <td className="text-end fw-medium">{formatMoney(row.amount, row.currency)}</td>
                                                <td className="text-center">
                                                    {row.users.active}<span className="text-muted">/{row.users.contracted}</span>
                                                    {row.users.active > row.users.contracted && <div className="small text-warning-emphasis">+{row.users.active - row.users.contracted}</div>}
                                                </td>
                                                <td className="small">
                                                    {row.card
                                                        ? <><i className={`bi ${cardBrandIcon(row.card.brand)} me-1 text-primary`} />•••• {row.card.last4}</>
                                                        : <span className="text-danger"><i className="bi bi-credit-card me-1" />Sin tarjeta</span>}
                                                </td>
                                                <td className="small">
                                                    {row.lastPayment
                                                        ? <>{new Date(row.lastPayment.at).toLocaleDateString('es-CR')}<div className="text-muted">{formatMoney(row.lastPayment.amount, row.currency)}</div></>
                                                        : <span className="text-muted">Sin pagos</span>}
                                                </td>
                                            </tr>
                                            {open && (
                                                <tr className="bg-light">
                                                    <td colSpan={columns} className="px-4 py-3">
                                                        <div className="d-flex flex-wrap gap-4 small mb-3">
                                                            <span><span className="text-muted">Registro:</span> {row.createdAt ? new Date(row.createdAt).toLocaleDateString('es-CR') : '-'}</span>
                                                            {row.trialEndsOn && <span><span className="text-muted">Fin de la prueba:</span> {formatDay(row.trialEndsOn)}</span>}
                                                            <span><span className="text-muted">Usuarios que se cobran:</span> {row.users.billed}</span>
                                                            {row.card?.expiration && <span><span className="text-muted">Tarjeta vence:</span> {row.card.expiration.slice(0, 2)}/{row.card.expiration.slice(2)}</span>}
                                                            {row.nextRetryAt && row.status === 'MOROSA' && <span><span className="text-muted">Próximo reintento:</span> {new Date(row.nextRetryAt).toLocaleString('es-CR')}</span>}
                                                            {row.suspendedAt && <span><span className="text-muted">Suspendida el:</span> {new Date(row.suspendedAt).toLocaleDateString('es-CR')}</span>}
                                                            {row.lastError && <span className="text-danger"><span className="text-muted">Último rechazo:</span> {row.lastError}</span>}
                                                        </div>
                                                        <h6 className="small text-uppercase text-muted mb-2">Historial de cobros</h6>
                                                        {history === 'loading' || history === undefined ? (
                                                            <span className="spinner-border spinner-border-sm text-primary" />
                                                        ) : history === 'error' ? (
                                                            <span className="small text-danger">No se pudo cargar el historial.</span>
                                                        ) : history.length === 0 ? (
                                                            <span className="small text-muted">Aún no hay cobros.</span>
                                                        ) : (
                                                            <table className="table table-sm small mb-0 bg-white">
                                                                <thead>
                                                                    <tr className="text-muted">
                                                                        <th>Fecha</th><th>Periodo</th><th className="text-end">Monto</th><th className="text-center">Usuarios</th>
                                                                        <th>Tarjeta</th><th>Resultado</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {history.map((c, i) => (
                                                                        <tr key={i}>
                                                                            <td>{new Date(c.at).toLocaleString('es-CR')}</td>
                                                                            <td>{formatDay(c.periodFrom)} – {formatDay(c.periodTo)}</td>
                                                                            <td className="text-end">{formatMoney(c.amount, c.currency)}</td>
                                                                            <td className="text-center">{c.users}</td>
                                                                            <td>{c.cardLast4 ? `•••• ${c.cardLast4}` : '-'}</td>
                                                                            <td className={CHARGE_STATUS[c.status] ?? ''}>
                                                                                {c.status === 'APROBADO' ? 'Aprobado' : c.status === 'RECHAZADO' ? 'Rechazado' : c.status === 'SIN_CARGO' ? 'Sin cargo' : c.status === 'PENDIENTE' ? 'Pendiente' : 'Error'}
                                                                                {c.message && <span className="text-muted"> · {c.message}</span>}
                                                                                {c.origin === 'CAMBIO_TARJETA' && <span className="text-muted"> · cambio de tarjeta</span>}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span className="small text-muted">{data?.total ?? 0} comercios</span>
                        {totalPage > 1 && <Paginator totalPage={totalPage} pageActual={page} onChange={setPage} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionsReportPage;
